'use client'

import * as React from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { Bookmark } from 'lucide-react'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { useAuth } from '@/components/auth'
import { navPillCls, navPillBadgeNeutralCls, navPillLabelCls } from './nav-pill'
import { useI18n } from '@/lib/i18n'

// Header Saved-searches shortcut with a count badge (same nav-pill vocabulary
// as LikedButton / NotificationBell).
//
// Saved searches have no global store — /searches keeps them in local page
// state — so this fetches its own count. Two guards keep that cheap:
//   * module-level cache keyed by uid, so remounting the header (every client
//     navigation) reuses the number instead of refetching;
//   * a 60s freshness window, refreshed on window focus and whenever something
//     dispatches `saved-searches-changed` (save dialog / delete on /searches).
// The badge counts SAVED SEARCHES — not unread matches; that is the bell's job.
const CACHE: { uid: string | null; count: number; at: number } = { uid: null, count: 0, at: 0 }
const TTL_MS = 60_000

export function SavedSearchesButton() {
  const pathname = usePathname()
  const { t } = useI18n()
  const { user } = useAuth()
  const [count, setCount] = React.useState(() => (CACHE.uid ? CACHE.count : 0))
  const inFlightRef = React.useRef(false)

  const fetchCount = React.useCallback(
    async (force = false) => {
      if (!user) {
        CACHE.uid = null
        CACHE.count = 0
        setCount(0)
        return
      }
      const fresh = CACHE.uid === user.uid && Date.now() - CACHE.at < TTL_MS
      if (!force && fresh) {
        setCount(CACHE.count)
        return
      }
      if (inFlightRef.current) return
      inFlightRef.current = true
      try {
        const list = await api.getSavedSearches()
        const n = Array.isArray(list) ? list.length : 0
        CACHE.uid = user.uid
        CACHE.count = n
        CACHE.at = Date.now()
        setCount(n)
      } catch {
        // Offline / API down / 401 — keep the last known number rather than
        // flashing a wrong 0. A guest is handled by the !user branch above.
      } finally {
        inFlightRef.current = false
      }
    },
    [user]
  )

  React.useEffect(() => {
    fetchCount()
  }, [fetchCount])

  React.useEffect(() => {
    if (!user) return
    const onChanged = () => fetchCount(true)
    const onFocus = () => fetchCount()
    window.addEventListener('saved-searches-changed', onChanged)
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('saved-searches-changed', onChanged)
      window.removeEventListener('focus', onFocus)
    }
  }, [user, fetchCount])

  // Landing on /searches is the authoritative moment — that page just listed
  // them, so re-sync in case a search was created elsewhere.
  const onSearchesPage = pathname.startsWith('/searches')
  React.useEffect(() => {
    if (onSearchesPage) fetchCount(true)
  }, [onSearchesPage, fetchCount])

  const label =
    count > 0
      ? `${t('header.savedSearches', 'Saved searches')} (${count})`
      : t('header.savedSearches', 'Saved searches')

  return (
    <Link
      href="/searches"
      className={cn(navPillCls(onSearchesPage), 'hidden xl:inline-flex')}
      aria-label={label}
      title={label}
    >
      <Bookmark className={cn('h-[18px] w-[18px]', onSearchesPage && 'fill-current')} />
      <span className={navPillLabelCls}>{t('header.searches', 'Searches')}</span>
      {count > 0 && <span className={navPillBadgeNeutralCls}>{count > 99 ? '99+' : count}</span>}
    </Link>
  )
}
